import React, { useState, useRef } from 'react';
import { Button } from 'primereact/button';
import { ListBox } from 'primereact/listbox';
import { Skeleton } from 'primereact/skeleton';
import { Message } from 'primereact/message';
import { Toast } from 'primereact/toast';
import { ShippingLineItem, ShippingOrder } from '../../../DeShippingTableWrapper';
import { LineItemCard } from './LineItemCard';
import { useLineItems } from '../../../hooks/useLineItems';
import { useOrderData } from '../../../hooks/useOrderData';
import { useLineItemImages } from '../../../hooks/useLineItemImages';
import { AcceptConfirmationDialog } from './AcceptConfirmationDialog';
import { DeclineReasonDialog } from './DeclineReasonDialog';
import { useOrderSelection } from '../../../hooks/useOrderSelection';

interface LineItemNavigatorProps {
  orderData: ReturnType<typeof useOrderData>;
  orderSelection: ReturnType<typeof useOrderSelection>;
  lineItemsHook: ReturnType<typeof useLineItems>;
  lineItemImagesHook: ReturnType<typeof useLineItemImages>;
}

export const LineItemNavigator: React.FC<LineItemNavigatorProps> = ({
  orderData,
  orderSelection,
  lineItemsHook,
  lineItemImagesHook,
}) => {
  const order = orderSelection.selectedOrder;
  const { 
    lineItems, 
    loading, 
    selectedLineItem, 
    setSelectedLineItem,
    acceptLineItem,
    declineLineItem,
    acceptAllLineItems,
    declineAllLineItems,
    statusUpdating,
    statusUpdateError,
    refetch
  } = lineItemsHook;
  
  const [showDeclineDialog, setShowDeclineDialog] = useState(false);
  const [showAcceptAllDialog, setShowAcceptAllDialog] = useState(false);

  const toast = useRef<Toast>(null);

  // Calculate progress based on item statuses
  const getReviewedCount = () => {
    return lineItems.filter(item => 
      item.fulfillment_status === 'accepted_awaiting_inspection'
    ).length;
  };


  const handleAcceptAllClick = async () => {
    // Check if there are any eligible items to accept before showing dialog
    const eligibleItems = lineItems.filter(item => item.fulfillment_status === 'accepted_awaiting_inspection');
    
    if (eligibleItems.length === 0) {
      toast.current?.show({
        severity: 'warn',
        summary: 'No Items to Accept',
        detail: 'No line items awaiting review to accept',
        life: 5000
      });
      return;
    }
    
    // Show accept all confirmation dialog
    setShowAcceptAllDialog(true);
  };

  const handleAcceptAllConfirm = async () => {
    try {
      const result = await acceptAllLineItems();
      
      if (result.success) {
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: result.message,
          life: 3000
        });
        setShowAcceptAllDialog(false);
        refetch();
        orderData.refreshSingleOrder(order?.order_id!)
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: result.message,
          life: 5000
        });
      }
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Error accepting all items',
        life: 5000
      });
    }
  };

  const handleAcceptAllCancel = () => {
    setShowAcceptAllDialog(false);
  };

  const handleDeclineAllClick = () => {
    // Check if there are any eligible items to decline before showing dialog
    const eligibleItems = lineItems.filter(item => item.fulfillment_status === 'accepted_awaiting_inspection');
    
    if (eligibleItems.length === 0) {
      toast.current?.show({
        severity: 'warn',
        summary: 'No Items to Decline',
        detail: 'No line items awaiting review to decline',
        life: 5000
      });
      return;
    }
    
    setShowDeclineDialog(true);
  };

  const handleDeclineConfirm = async (reason: string) => {
    if (!reason.trim()) return;

    try {
      const result = await declineAllLineItems(reason);
      
      if (result.success) {
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: result.message,
          life: 3000
        });
        setShowDeclineDialog(false);
        refetch();
        orderData.refreshSingleOrder(order?.order_id!)
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: result.message,
          life: 5000
        });
      }
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Error during decline operation',
        life: 5000
      });
    }
  };

  const handleDeclineCancel = () => {
    setShowDeclineDialog(false);
  };

  // Convert selectedLineItemId to the format expected by ListBox
  const selectedItem = selectedLineItem ? lineItems.find(item => item.workflow_id === selectedLineItem.workflow_id) : null;

  // Handle ListBox selection change
  const handleSelectionChange = (e: { value: ShippingLineItem | null }) => {
    if (e.value) {
      setSelectedLineItem(e.value);
    }
    // Note: ListBox doesn't naturally support deselection, but we can handle it in the item template
  };

  // Custom item template for ListBox
  const itemTemplate = (item: ShippingLineItem) => {
    const isSelected = item.workflow_id === selectedLineItem?.workflow_id;
    const index = lineItems.findIndex(lineItem => lineItem.workflow_id === item.workflow_id);
    const isLastItem = index === lineItems.length - 1;

    return (
      <LineItemCard
        lineItemImagesHook={lineItemImagesHook}
        item={item}
        isSelected={isSelected}
        isLastItem={isLastItem}
      />
    );
  };

  // Skeleton component for loading state
  const SkeletonLineItem = () => (
    <div className="p-3 border-1 border-300 border-round mb-2">
      <div className="flex justify-content-between align-items-start mb-3">
        <div className="flex-1">
          <Skeleton width="60%" height="1.2rem" className="mb-2" />
          <Skeleton width="40%" height="1rem" className="mb-1" />
          <Skeleton width="30%" height="1rem" />
        </div>
        <div className="flex gap-2 ml-3">
          <Skeleton width="80px" height="2rem" />
          <Skeleton width="80px" height="2rem" />
        </div>
      </div>
      <div className="flex justify-content-between align-items-center">
        <Skeleton width="50%" height="1rem" />
        <Skeleton width="100px" height="1rem" />
      </div>
    </div>
  );

  const reviewedCount = getReviewedCount();

  return (
    <div className="h-full flex flex-column">
      <Toast ref={toast} />
      
      {/* Header Section */}
      <div className="mb-3 pb-3 border-bottom-1 border-300 sticky top-0 z-1">
        <h5 className="m-0 mb-2 text-900">Order: {order?.order_name}</h5>
        {loading ? (
          <Skeleton width="60%" height="1rem" className="mb-3" />
        ) : (
          <p className="text-sm text-600 m-0 mb-3">
            Progress: {reviewedCount} of {lineItems.length} line items pending review
          </p>
        )}
        
        {/* Error Message */}
        {statusUpdateError && (
          <Message 
            severity="error" 
            text={statusUpdateError} 
            className="mb-3"
          />
        )}
        
        {/* Batch Actions */}
        <div className="flex px-3">
          <Button
            label="Accept All"
            icon="pi pi-check-circle"
            size="small"
            className="p-button-success p-button-outlined flex-1"
            style={{ marginRight: '8px' }}
            disabled={loading || statusUpdating}
            loading={statusUpdating}
            onClick={handleAcceptAllClick}
          />
          <Button
            label="Decline All"
            icon="pi pi-times-circle"
            size="small"
            className="p-button-danger p-button-outlined flex-1"
            style={{ marginLeft: '4px', marginRight: '4px' }}
            onClick={handleDeclineAllClick}
            disabled={loading || statusUpdating}
          />
        </div>
      </div>

      {/* Line Items ListBox or Skeleton */}
      <div className="flex-1 overflow-auto px-2" style={{ maxHeight: 'calc(100vh - 300px)' }}>
        {loading ? (
          <SkeletonLineItem />
        ) : (
          <ListBox
            value={selectedItem}
            options={lineItems}
            onChange={handleSelectionChange}
            itemTemplate={itemTemplate}
            optionLabel="product_sku"
            className="w-full border-none p-0"
            listStyle={{ 
              maxHeight: 'none',
              border: 'round-lg',
              padding: '0',
              backgroundColor: 'transparent'
            }}
            style={{
              border: 'none',
              backgroundColor: 'transparent'
            }}
          />
        )}
      </div>

      {/* Dialog Components */}
      <AcceptConfirmationDialog
        visible={showAcceptAllDialog}
        onHide={handleAcceptAllCancel}
        onConfirm={handleAcceptAllConfirm}
        order={order}
        totalItems={lineItems.length}
        acceptType="all"
        loading={statusUpdating}
      />

      <DeclineReasonDialog
        visible={showDeclineDialog}
        onHide={handleDeclineCancel}
        onConfirm={handleDeclineConfirm}
        totalItems={lineItems.length}
        declineType="all"
        loading={statusUpdating}
      />
    </div>
  );
};